/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Router lib
import {
	createBrowserRouter,
	RouterProvider,
} from 'react-router';

// Routes
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';
import ErrorPage from '@app/routes/error/error-page';
import MainPage from '@app/routes/app/main/main';

// Constants
import { ROLES } from '@config/constants/roles';
import { paths } from '@config/constants/paths';

// Pages

import AuthPage from '@app/routes/auth/login/login';
import AuthArecPage from '@app/routes/auth-arec/login/login';
import ProductionsPage from '@app/routes/app/sourcing/productions/productions';
import OffsetsPage from '@app/routes/app/offsets/offsets';
import ApiTokensPage from '@app/routes/app/resources/api-tokens/api-tokens';
import MintHistoryPage from '@app/routes/app/sourcing/mint-history/mint-history';

import UpdatePasswordPage from '@app/routes/auth/update-password/update-password';

import UpdateEmailPage from '@app/routes/auth/update-email/update-email';

import ConversionFactorPage from '@app/routes/app/resources/conversion-factors/conversion-factors';

import MeasurabilityProjectsPage from '@app/routes/app/catalogs/measurability-projects/list/measurability-projects';

import MeasurabilityProjectDetailsPage from '@app/routes/app/catalogs/measurability-projects/details/measurability-project-details';

import AdditionalityProjectsPage from '@app/routes/app/catalogs/additionality-projects/list/additionality-projects';

import AdditionalityProjectDetailsPage from '@app/routes/app/catalogs/additionality-projects/details/additionality-project-details';

import MeterPointsPage from '@app/routes/app/resources/meter-points/meter-points';

import SitesPage from '@app/routes/app/resources/sites/sites';

import ImportHistoryPage from '@app/routes/app/sourcing/import-history/import-history';

import MeasurabilitiesPage from '@app/routes/app/resources/measurabilities/measurabilities';

import AdditionalitiesPage from '@app/routes/app/resources/additionalities/additionalities';

import PublicMeasurabilityProjectsPage from '@app/routes/public/catalogs/measurability-projects/measurability-projects';

import PublicMeasurabilityProjectDetailsPage from '@app/routes/public/catalogs/measurability-project-details/measurability-project-details';

import PublicAdditionalityProjectsPage from '@app/routes/public/catalogs/additionality-projects/additionality-projects';

import PublicAdditionalityProjectDetailsPage from '@app/routes/public/catalogs/additionality-project-details/additionality-project-details';

import DashboardPage from '@app/routes/app/dashboard/dashboard';

import ProducersPage from '@app/routes/app/resources/producers/producers';

import BrandsPage from '@app/routes/app/resources/brands/brands';

import OrganizationsPage from '@app/routes/app/resources/organizations/organizations';

import UsersPage from '@app/routes/app/resources/users/users';

import AccountSettingsPage from '@app/routes/app/account-settings/account-settings';

/* -------------------------------------------------------------------------- */
/*                                    Router                                  */
/* -------------------------------------------------------------------------- */

const router = createBrowserRouter([
	{
		path: paths.app.root.path,
		element: (
			<ProtectedRoute>
				<MainPage />
			</ProtectedRoute>
		),
		errorElement: <ErrorPage />,
		children: [
			{
				path: paths.app.dashboard.path,
				element: (
					<ProtectedRoute>
						<DashboardPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.accountSettings.path,
				element: (
					<ProtectedRoute>
						<AccountSettingsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.measurabilities.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
					>
						<MeasurabilitiesPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.additionalities.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
					>
						<AdditionalitiesPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.productions.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.ORGANIZATION_ADMIN,
							ROLES.ORGANIZATION_STAFF,
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
						]}
					>
						<ProductionsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.mintHistory.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
					>
						<MintHistoryPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.importHistory.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
					>
						<ImportHistoryPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.offsets.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.USER,
							ROLES.ORGANIZATION_ADMIN,
							ROLES.ORGANIZATION_MEMBER,
							ROLES.ORGANIZATION_STAFF,
							ROLES.BRAND_ADMIN,
							ROLES.BRAND_STAFF,
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
						]}
					>
						<OffsetsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.sites.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
					>
						<SitesPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.apiTokens.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.PRODUCER_ADMIN,
						]}
					>
						<ApiTokensPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.meterPoints.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
					>
						<MeterPointsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.producers.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.ORGANIZATION_ADMIN,
							ROLES.ORGANIZATION_STAFF,
						]}
					>
						<ProducersPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.brands.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.ORGANIZATION_ADMIN,
							ROLES.ORGANIZATION_STAFF,
						]}
					>
						<BrandsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.users.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
					>
						<UsersPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.organizations.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
					>
						<OrganizationsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.conversionFactors.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						]}
					>
						<ConversionFactorPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.measurabilityProjects.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.ORGANIZATION_ADMIN,
							ROLES.ORGANIZATION_MEMBER,
							ROLES.ORGANIZATION_STAFF,
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
							ROLES.BRAND_ADMIN,
							ROLES.BRAND_STAFF,
						]}
					>
						<MeasurabilityProjectsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.measurabilityProjectDetails.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.ORGANIZATION_ADMIN,
							ROLES.ORGANIZATION_MEMBER,
							ROLES.ORGANIZATION_STAFF,
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
							ROLES.BRAND_ADMIN,
							ROLES.BRAND_STAFF,
						]}
					>
						<MeasurabilityProjectDetailsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.additionalityProjects.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.ORGANIZATION_ADMIN,
							ROLES.ORGANIZATION_MEMBER,
							ROLES.ORGANIZATION_STAFF,
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
							ROLES.BRAND_ADMIN,
							ROLES.BRAND_STAFF,
						]}
					>
						<AdditionalityProjectsPage />
					</ProtectedRoute>
				),
			},
			{
				path: paths.app.additionalityProjectDetails.path,
				element: (
					<ProtectedRoute
						roles={[
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
							ROLES.ORGANIZATION_ADMIN,
							ROLES.ORGANIZATION_MEMBER,
							ROLES.ORGANIZATION_STAFF,
							ROLES.PRODUCER_ADMIN,
							ROLES.PRODUCER_STAFF,
							ROLES.BRAND_ADMIN,
							ROLES.BRAND_STAFF,
						]}
					>
						<AdditionalityProjectDetailsPage />
					</ProtectedRoute>
				),
			},
		],
	},
	{
		path: paths.public.catalogMeasurabilityProjects.path,
		element: <PublicMeasurabilityProjectsPage />,
	},
	{
		path: paths.public.catalogMeasurabilityProjectDetails
			.path,
		element: <PublicMeasurabilityProjectDetailsPage />,
	},
	{
		path: paths.public.catalogAdditionalityProjects.path,
		element: <PublicAdditionalityProjectsPage />,
	},
	{
		path: paths.public.catalogAdditionalityProjectDetails
			.path,
		element: <PublicAdditionalityProjectDetailsPage />,
	},
	{
		path: paths.auth.login.path,
		element: (
			<PublicRoute>
				<AuthPage />
			</PublicRoute>
		),
	},
	{
		path: paths.auth.loginArec1.path,
		element: (
			<PublicRoute>
				<AuthArecPage />
			</PublicRoute>
		),
	},
	{
		path: paths.auth.loginArec2.path,
		element: (
			<PublicRoute>
				<AuthArecPage />
			</PublicRoute>
		),
	},
	{
		path: paths.auth.signIn.path,
		element: (
			<PublicRoute>
				<UpdatePasswordPage />
			</PublicRoute>
		),
	},
	{
		path: paths.auth.updatePassword.path,
		element: (
			<PublicRoute>
				<UpdatePasswordPage />
			</PublicRoute>
		),
	},
	{
		path: paths.auth.updateEmail.path,
		element: (
			<PublicRoute>
				<UpdateEmailPage />
			</PublicRoute>
		),
	},
]);

const AppRouter = () => (
	<RouterProvider router={router} />
);
export default AppRouter;
